import { z } from 'zod';
import { baseBusinessApplicant, businessDetails, refineTrustDeedRequired } from '../shared';
import { isExtendedStructure } from '../types';

// Inline required-field message to avoid circular dependency:
// importing requiredFieldError from '../helper' causes a cycle via shared/model/address.ts
// because businessDetails is the first module loaded from form/index.ts.
const requiredFieldError = (fieldLabel: string) => `${fieldLabel} is a required field`;

// Type guard that narrows the enterprise discriminated union to extended structure variants
type ExtendedEnterprise = Extract<z.infer<typeof businessDetails>, { billingContactSameAsPrimary: boolean }>;
const isExtendedEnterprise = (enterprise: z.infer<typeof businessDetails>): enterprise is ExtendedEnterprise =>
  isExtendedStructure(enterprise.structure);

export const businessDetailsFormSchema = z
  .object({
    enterprise: businessDetails,
    applicant: baseBusinessApplicant.pick({
      type: true,
      firstName: true,
      middleName: true,
      lastName: true,
    }),
    isSame: z.boolean().optional(),
    customerDeclared: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (isExtendedEnterprise(data.enterprise)) {
      const { billingContactSameAsPrimary, billingContact } = data.enterprise;

      if (!billingContactSameAsPrimary) {
        if (!billingContact?.firstName) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredFieldError('First name'),
            path: ['enterprise', 'billingContact', 'firstName'],
          });
        }
        if (!billingContact?.lastName) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredFieldError('Last name'),
            path: ['enterprise', 'billingContact', 'lastName'],
          });
        }
        if (!billingContact?.residentialAddress) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredFieldError('Residential address'),
            path: ['enterprise', 'billingContact', 'residentialAddress'],
          });
        }
      }

      refineTrustDeedRequired(data.enterprise, ctx);

      if (!data.customerDeclared) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredFieldError('Customer declaration'),
          path: ['customerDeclared'],
        });
      }
    }
  });

export type BusinessDetailsType = z.infer<typeof businessDetailsFormSchema>;
